
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, Plus, FileText, Upload, Search, Filter, Eye, Edit, Trash2, MessageSquare, FileText as FileTextIcon, Download, Paperclip } from "lucide-react";
import { useSinistrosPaginated, useAtualizarStatusSinistro, useAtualizarSinistro, useCriarSinistro, useExcluirSinistro } from "@/hooks/useSinistros";
import { ClienteSearchSelect } from "@/components/ClienteSearchSelect";
import { useAuth } from "@/hooks/useAuth";
import { useSinistroComentarios, useCriarComentario } from "@/hooks/useSinistroComentarios";
import { hasPermission } from "@/types/auth";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useQueryClient } from "@tanstack/react-query";
import { uploadFileToStorage, openStorageUrl } from "@/lib/storageUrl";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  criarDespesaAutomaticaFromSinistro,
  getValorSinistroParaDespesa,
  parseValorMonetarioBr,
} from "@/lib/sinistroDespesa";
import {
  formatarMoedaBr,
  getValorTotalSinistro,
  isItemValorAnexo,
  isItensValorColumnMissing,
  itemValorToAnexoString,
  novoItemValor as criarItemValor,
  parseItensValorFromSinistro,
  somarItensValor,
  type SinistroItemValor,
} from "@/lib/sinistroValores";

// Componente para exibir histórico de comentários
function SinistroHistorico({ sinistroId }: { sinistroId: number }) {
  const { data: comentarios = [], isLoading, isError, error } = useSinistroComentarios(sinistroId);

  if (isLoading) {
    return (
      <div className="bg-gray-50 p-4 rounded-lg">
        <p className="text-gray-600 text-sm">Carregando histórico...</p>
      </div>
    );
  }

  if (isError) {
    const msg = error instanceof Error ? error.message : "Erro ao carregar comentários";
    return (
      <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
        <p className="text-red-800 text-sm">{msg}</p>
      </div>
    );
  }

  if (comentarios.length === 0) {
    return (
      <div className="bg-gray-50 p-4 rounded-lg">
        <p className="text-gray-600 text-sm">Nenhum comentário encontrado.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {comentarios.map((comentario) => (
        <div key={comentario.id} className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  comentario.tipo === 'comentario' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {comentario.tipo === 'comentario' ? 'Comentário' : 'Observação'}
                </div>
                <span className="text-xs text-gray-500">
                  {format(new Date(comentario.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                </span>
              </div>
              <p className="text-gray-900">{comentario.conteudo}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ListagemAcoes, type ListagemColumn } from "@/components/listagem/ListagemAcoes";
import { listagemThCenter, listagemTdCenter } from "@/components/listagem/listagemTableClasses";
import { cn } from "@/lib/utils";
import { BRASIL_UFS } from "@/lib/estados";
import { useCidadesPorEstado } from "@/hooks/useClientesEstadosCidades";
import type { SinistrosFiltroParams } from "@/hooks/useSinistros";

type AnexoSinistro = { url: string; nome: string };

function parseAnexoSinistro(item: string): AnexoSinistro {
  try {
    const parsed = JSON.parse(item) as { url?: string; nome?: string };
    if (parsed?.url) {
      return { url: parsed.url, nome: parsed.nome || nomeFromAnexoUrl(parsed.url) };
    }
  } catch {
    // legado: URL pura em text[]
  }
  return { url: item, nome: nomeFromAnexoUrl(item) };
}

function nomeFromAnexoUrl(url: string): string {
  try {
    const path = decodeURIComponent(new URL(url).pathname);
    const base = path.split("/").pop() || "anexo";
    const idx = base.indexOf("_");
    return idx >= 0 ? base.slice(idx + 1) : base;
  } catch {
    return "anexo";
  }
}

async function uploadAnexosSinistro(files: File[]): Promise<string[]> {
  const urls: string[] = [];
  for (const file of files) {
    const fileName = `sinistros/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`;
    try {
      const { publicUrl } = await uploadFileToStorage(fileName, file, {
        buckets: ["uploads"],
      });
      urls.push(JSON.stringify({ url: publicUrl, nome: file.name }));
    } catch (uploadError) {
      const message = uploadError instanceof Error ? uploadError.message : "Erro desconhecido";
      toast.error(`Erro ao fazer upload de ${file.name}: ${message}`);
    }
  }
  return urls;
}

export function Sinistros() {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [searchTermInput, setSearchTermInput] = useState(""); // Estado local para o input
  const [searchTerm, setSearchTerm] = useState(""); // Estado para a query (com debounce)
  const filtrosDraftDefault = useMemo(
    () => ({
      dataInicio: "",
      dataFim: "",
      status: "todos",
      tipo: "",
      valorMin: "",
      valorMax: "",
      estado: "",
      cidade: "",
    }),
    []
  );
  const [filtrosDraft, setFiltrosDraft] = useState(filtrosDraftDefault);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosDraftDefault);
  const { data: cidadesDoEstado = [] } = useCidadesPorEstado(filtrosDraft.estado);

  const toNum = (v: string) => {
    const s = (v ?? "").trim();
    if (!s) return undefined;
    const n = Number(s.replace(/\./g, "").replace(",", "."));
    return Number.isFinite(n) ? n : undefined;
  };

  const filtrosHook = useMemo<SinistrosFiltroParams>(() => {
    const periodo =
      filtrosAplicados.dataInicio || filtrosAplicados.dataFim
        ? { dataInicio: filtrosAplicados.dataInicio || undefined, dataFim: filtrosAplicados.dataFim || undefined }
        : null;
    const valorMin = toNum(filtrosAplicados.valorMin);
    const valorMax = toNum(filtrosAplicados.valorMax);
    return {
      periodo,
      status: filtrosAplicados.status === "todos" ? null : filtrosAplicados.status,
      tipo: filtrosAplicados.tipo || null,
      valorMin: typeof valorMin === "number" ? valorMin : null,
      valorMax: typeof valorMax === "number" ? valorMax : null,
      estado: filtrosAplicados.estado || null,
      cidade: filtrosAplicados.cidade || null,
    };
  }, [filtrosAplicados]);
  
  // Debounce: atualizar busca após 700ms sem digitação (reduz significativamente requisições ao banco)
  useEffect(() => {
    // Se o campo estiver vazio, limpar busca imediatamente
    if (searchTermInput === "") {
      setSearchTerm("");
      return;
    }

    // Para campos com texto, aguardar delay para evitar queries excessivas
    const timer = setTimeout(() => {
      setSearchTerm(searchTermInput);
    }, 700);

    return () => clearTimeout(timer);
  }, [searchTermInput]);
  
  // Buscar sinistros paginados com busca e filtros
  const { data: sinistrosResult, isLoading, error, refetch } = useSinistrosPaginated(currentPage, itemsPerPage, searchTerm, filtrosHook);
  const sinistros = sinistrosResult?.data || [];
  const totalSinistros = sinistrosResult?.count || 0;
  const handleAplicarFiltros = () => {
    setFiltrosAplicados(filtrosDraft);
    setCurrentPage(1);
  };

  const handleLimparFiltros = () => {
    setFiltrosDraft(filtrosDraftDefault);
    setFiltrosAplicados(filtrosDraftDefault);
    setSearchTermInput("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const { profile, isAdmin, isTi, canDelete } = useAuth();
  const atualizarStatus = useAtualizarStatusSinistro();
  const atualizarSinistro = useAtualizarSinistro();
  const criarSinistro = useCriarSinistro();
  const excluirSinistro = useExcluirSinistro();
  const criarComentario = useCriarComentario();

  // Debug logs
  console.log('🔍 DEBUG - Componente Sinistros renderizado');
  type SinistroRow = (typeof currentItems extends (infer T)[] ? T : any) & {
    cliente_nome: string;
    cliente_estado: string;
    cliente_cidade: string;
    cliente_localizacao: string;
    tipo_label: string;
    status_label: string;
    valor_label: string;
    data_label: string;
  };
  const sinistroColumns: ListagemColumn<SinistroRow>[] = [
    { key: "id", label: "ID", value: (s) => s.id },
    { key: "cliente", label: "Cliente", value: (s) => s.cliente_nome },
    { key: "localizacao", label: "Estado/Cidade", value: (s) => s.cliente_localizacao },
    { key: "tipo", label: "Tipo", value: (s) => s.tipo_label },
    { key: "status", label: "Status", value: (s) => s.status_label },
    { key: "valor", label: "Valor", value: (s) => s.valor_label },
    { key: "data", label: "Data", value: (s) => s.data_label },
  ];
  const [visibleSinistroCols, setVisibleSinistroCols] = useState<string[]>(sinistroColumns.map((c) => c.key));
  const toggleSinistroCol = (key: string, nextVisible: boolean) => {
    setVisibleSinistroCols((prev) => (nextVisible ? Array.from(new Set([...prev, key])) : prev.filter((k) => k !== key)));
  };



  const [novoSinistro, setNovoSinistro] = useState({
    cliente_id: "",
    tipo: "",
    descricao: "",
    data_ocorrencia: "",
    valor_estimado: ""
  });

  // Estados para visualização e comentários
  const [sinistroSelecionado, setSinistroSelecionado] = useState<any>(null);
  const [showModalVisualizacao, setShowModalVisualizacao] = useState(false);
  const [modalSomenteLeitura, setModalSomenteLeitura] = useState(false);
  const [showModalNovoSinistro, setShowModalNovoSinistro] = useState(false);
  const [sinistroParaExcluir, setSinistroParaExcluir] = useState<{ id: number } | null>(null);
  const [comentario, setComentario] = useState("");
  const [anexos, setAnexos] = useState<File[]>([]);
  const [uploadingAnexos, setUploadingAnexos] = useState(false);
  const [showConfirmFinalizar, setShowConfirmFinalizar] = useState(false);
  const [sinistroPendenteFinalizar, setSinistroPendenteFinalizar] = useState<any>(null);
  const [anexosEdit, setAnexosEdit] = useState<File[]>([]);
  const [uploadingAnexosEdit, setUploadingAnexosEdit] = useState(false);
  const [novoItemDescricao, setNovoItemDescricao] = useState("");
  const [novoItemValor, setNovoItemValor] = useState("");

  // Debug logs após declaração das variáveis
  console.log('🔍 DEBUG - Estado do formulário:', novoSinistro);
  console.log('🔍 DEBUG - Modal aberto:', showModalNovoSinistro);
  console.log('🔍 DEBUG - Anexos:', anexos);

  // Funções para gerenciar anexos
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('🔍 DEBUG - handleFileSelect chamado');
    console.log('🔍 DEBUG - Files:', event.target.files);
    
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      console.log('🔍 DEBUG - FileArray:', fileArray);
      
      // Validar tipos de arquivo
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      const validFiles = fileArray.filter(file => {
        console.log('🔍 DEBUG - Validando arquivo:', file.name, file.type, file.size);
        
        if (!validTypes.includes(file.type)) {
          console.log('🔍 DEBUG - Tipo inválido:', file.type);
          toast.error(`Tipo de arquivo não suportado: ${file.name}`);
          return false;
        }
        if (file.size > 20 * 1024 * 1024) { // 20MB
          console.log('🔍 DEBUG - Arquivo muito grande:', file.size);
          toast.error(`Arquivo muito grande: ${file.name} (máx. 20MB)`);
          return false;
        }
        return true;
      });
      
      console.log('🔍 DEBUG - Arquivos válidos:', validFiles);
      setAnexos(prev => {
        const newAnexos = [...prev, ...validFiles];
        console.log('🔍 DEBUG - Novo estado de anexos:', newAnexos);
        return newAnexos;
      });
      toast.success(`${validFiles.length} arquivo(s) selecionado(s)`);
    } else {
      console.log('🔍 DEBUG - Nenhum arquivo selecionado');
    }
  };

  const removeAnexo = (index: number) => {
    setAnexos(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileSelectEdit = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    const validFiles = Array.from(files).filter((file) => {
      if (!validTypes.includes(file.type)) {
        toast.error(`Tipo de arquivo não suportado: ${file.name}`);
        return false;
      }
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`Arquivo muito grande: ${file.name} (máx. 20MB)`);
        return false;
      }
      return true;
    });
    if (validFiles.length > 0) {
      setAnexosEdit((prev) => [...prev, ...validFiles]);
      toast.success(`${validFiles.length} arquivo(s) selecionado(s)`);
    }
    event.target.value = "";
  };

  const removeAnexoEdit = (index: number) => {
    setAnexosEdit((prev) => prev.filter((_, i) => i !== index));
  };

  const resetCamposEdicao = () => {
    setAnexosEdit([]);
    setNovoItemDescricao("");
    setNovoItemValor("");
    setComentario("");
  };

  const inicializarCamposEdicao = () => {
    setAnexosEdit([]);
    setNovoItemDescricao("");
    setNovoItemValor("");
    setComentario("");
  };

  const persistirItensValor = async (itens: SinistroItemValor[]) => {
    if (!sinistroSelecionado) return;
    const total = somarItensValor(itens);
    const basePayload = {
      id: sinistroSelecionado.id.toString(),
      valor_estimado: total > 0 ? total : null,
      valor_aprovado: total > 0 ? total : null,
    };

    try {
      const atualizado = await atualizarSinistro.mutateAsync({
        ...basePayload,
        itens_valor: itens,
      });

      if ((atualizado as { _itensValorOmitido?: boolean })._itensValorOmitido) {
        const anexosReais = (sinistroSelecionado.anexos ?? []).filter((a: string) => !isItemValorAnexo(a));
        const comFallback = await atualizarSinistro.mutateAsync({
          ...basePayload,
          anexos: [...anexosReais, ...itens.map(itemValorToAnexoString)],
        });
        const merged = { ...comFallback, itens_valor: itens };
        setSinistroSelecionado(merged);
        return merged;
      }

      setSinistroSelecionado(atualizado);
      return atualizado;
    } catch (error) {
      if (isItensValorColumnMissing(error)) {
        const anexosReais = (sinistroSelecionado.anexos ?? []).filter((a: string) => !isItemValorAnexo(a));
        const comFallback = await atualizarSinistro.mutateAsync({
          ...basePayload,
          anexos: [...anexosReais, ...itens.map(itemValorToAnexoString)],
        });
        const merged = { ...comFallback, itens_valor: itens };
        setSinistroSelecionado(merged);
        return merged;
      }
      throw error;
    }
  };

  const handleAdicionarItemValor = async () => {
    if (!sinistroSelecionado) return;
    const descricao = novoItemDescricao.trim();
    const valor = parseValorMonetarioBr(novoItemValor);
    if (!descricao) {
      toast.error("Informe a descrição do item");
      return;
    }
    if (valor == null) {
      toast.error("Informe um valor válido para o item");
      return;
    }
    try {
      const itensAtuais = parseItensValorFromSinistro(sinistroSelecionado);
      await persistirItensValor([...itensAtuais, criarItemValor(descricao, valor)]);
      setNovoItemDescricao("");
      setNovoItemValor("");
      toast.success("Item adicionado com sucesso!");
    } catch (error: unknown) {
      console.error("Erro ao adicionar item de valor:", error);
      toast.error("Erro ao salvar item");
    }
  };

  const handleRemoverItemValor = async (itemId: string) => {
    if (!sinistroSelecionado) return;
    try {
      const itens = parseItensValorFromSinistro(sinistroSelecionado).filter((i) => i.id !== itemId);
      await persistirItensValor(itens);
      toast.success("Item removido");
    } catch (error) {
      console.error("Erro ao remover item de valor:", error);
      toast.error("Erro ao remover item");
    }
  };

  // Funções para comentários
  const handleAdicionarComentario = async () => {
    if (!comentario.trim() || !sinistroSelecionado) {
      toast.error('Digite um comentário');
      return;
    }

    try {
      await criarComentario.mutateAsync({
        sinistroId: Number(sinistroSelecionado.id),
        tipo: 'comentario',
        conteudo: comentario.trim()
      });

      setComentario('');
      toast.success('Comentário adicionado com sucesso!');
    } catch (error: unknown) {
      console.error('Erro ao adicionar comentário:', error);
      const msg = error instanceof Error ? error.message : String(error);
      if (msg.includes("sinistro_comentarios") && msg.includes("schema cache")) {
        toast.error(
          "Tabela de comentários não configurada. Execute a migration 20260519000001_create_sinistro_comentarios no Supabase (SQL Editor)."
        );
      } else {
        toast.error(msg || "Erro ao adicionar comentário");
      }
    }
  };

  // Data máxima para ocorrência (hoje)
  const dataMaxima = new Date().toISOString().split('T')[0];

  const getTipoSinistroLabel = (tipo: string) => {
    if (!tipo) return 'Não informado';
    
    // Mapear valores para formatos corretos
    switch (tipo.toLowerCase()) {
      case 'incendio': return 'Incêndio';
      case 'vendaval': return 'Vendaval';
      case 'granizo': return 'Granizo';
      case 'raio': return 'Dano Elétrico (Raio)';
      case 'explosao': return 'Explosão';
      case 'roubo_furto': return 'Roubo/Furto';
      case 'dano_eletrico': return 'Dano Elétrico';
      case 'inundacao': return 'Inundação';
      case 'queda_arvore': return 'Queda de Árvore';
      case 'acidente_veiculo': return 'Acidente com Veículo';
      case 'vandalismo': return 'Vandalismo';
      case 'fenomeno_natural': return 'Fenômeno Natural';
      case 'outros': return 'Outros';
      default: 
        // Formatação genérica para tipos não mapeados
        return tipo
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    }
  };

  // Resetar para página 1 quando busca ou filtro mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filtrosHook]);

  // Os sinistros já vêm filtrados do backend
  const sinistrosFiltrados = sinistros;

  // Calcular dados da paginação
  const totalItems = totalSinistros;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentItems = sinistrosFiltrados; // Já vem paginado e filtrado do backend

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aberto': return 'bg-yellow-100 text-yellow-800';
      case 'analise': return 'bg-blue-100 text-blue-800';
      case 'andamento': return 'bg-purple-100 text-purple-800';
      case 'finalizado': return 'bg-green-100 text-green-800';
      case 'rejeitado': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'aberto': return 'Aberto';
      case 'analise': return 'Análise';
      case 'andamento': return 'Andamento';
      case 'finalizado': return 'Finalizado';
      case 'rejeitado': return 'Rejeitado';
      default: return status;
    }
  };

  // Função para verificar se o perfil está carregado
  const isProfileLoaded = () => {
    return profile !== null && profile !== undefined;
  };

  // Função para verificar se um sinistro pode ser editado
  const canEditSinistro = (sinistro: any) => {
    // Verificar se o sinistro existe
    if (!sinistro) {
      console.error('Sinistro não encontrado:', sinistro);
      return false;
    }

    // Verificar se o status existe, usar 'aberto' como padrão
    const status = sinistro.status || 'aberto';
    return status !== 'finalizado';
  };

  // Função para verificar se o usuário pode finalizar sinistros
  const canFinalizeSinistro = () => {
    return isAdmin();
  };

  const executarFinalizacao = async (sinistro: any) => {
    await atualizarStatus.mutateAsync({
      id: sinistro.id.toString(),
      status: "finalizado",
    });
    await queryClient.invalidateQueries({ queryKey: ["sinistros"] });

    const resultado = await criarDespesaAutomaticaFromSinistro(sinistro, getTipoSinistroLabel);
    if (resultado.criada) {
      await queryClient.invalidateQueries({ queryKey: ["transacoes_financeiras"] });
      toast.success("Sinistro finalizado e conta a pagar gerada no financeiro.");
    } else if (resultado.motivo === "ja_existe") {
      toast.success("Sinistro finalizado. Conta a pagar já existia para este sinistro.");
    } else if (resultado.motivo === "sem_valor") {
      toast.success("Sinistro finalizado. Informe um valor antes de finalizar para gerar conta a pagar.");
    } else {
      toast.success("Sinistro finalizado com sucesso!");
    }
  };

  const limparFluxoFinalizar = () => {
    setShowConfirmFinalizar(false);
    setSinistroPendenteFinalizar(null);
  };

  const handleConfirmarFinalizar = async () => {
    if (!sinistroPendenteFinalizar) return;
    try {
      await executarFinalizacao(sinistroPendenteFinalizar);
      limparFluxoFinalizar();
      if (showModalVisualizacao && sinistroSelecionado?.id === sinistroPendenteFinalizar.id) {
        setSinistroSelecionado({ ...sinistroSelecionado, status: "finalizado" });
      }
    } catch (error) {
      console.error("Erro ao finalizar sinistro:", error);
      toast.error("Erro ao finalizar sinistro");
    }
  };

  const handleSalvarAnexos = async () => {
    if (!sinistroSelecionado || anexosEdit.length === 0) {
      toast.error("Selecione ao menos um arquivo para salvar");
      return;
    }
    try {
      setUploadingAnexosEdit(true);
      const novosUrls = await uploadAnexosSinistro(anexosEdit);
      if (novosUrls.length === 0) return;

      const anexosAtuais = Array.isArray(sinistroSelecionado.anexos) ? sinistroSelecionado.anexos : [];
      const anexosMesclados = [...anexosAtuais, ...novosUrls];

      const atualizado = await atualizarSinistro.mutateAsync({
        id: sinistroSelecionado.id.toString(),
        anexos: anexosMesclados,
      });

      setSinistroSelecionado(atualizado);
      setAnexosEdit([]);
      toast.success("Anexos salvos com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar anexos:", error);
      toast.error("Erro ao salvar anexos");
    } finally {
      setUploadingAnexosEdit(false);
    }
  };

  const handleStatusChange = async (sinistroId: string | number, novoStatus: string) => {
    console.log('🔍 DEBUG - handleStatusChange chamado:', { sinistroId, novoStatus, tipo: typeof sinistroId });
    
    // Verificar se o perfil está carregado
    if (!isProfileLoaded()) {
      console.log('🔍 DEBUG - Perfil não carregado');
      toast.error('Perfil do usuário não carregado. Tente novamente.');
      return;
    }

    // Encontrar o sinistro para verificar se pode ser editado
    console.log('🔍 DEBUG - Procurando sinistro com ID:', sinistroId);
    console.log('🔍 DEBUG - Sinistros disponíveis:', sinistros.map(s => ({ id: s.id, tipo: typeof s.id })));
    
    const sinistro = sinistros.find(s => s.id == sinistroId); // Usar == para comparar string/number
    console.log('🔍 DEBUG - Sinistro encontrado:', sinistro);
    
    if (!sinistro) {
      console.log('🔍 DEBUG - Sinistro não encontrado para ID:', sinistroId);
      toast.error('Sinistro não encontrado');
      return;
    }

    if (!canEditSinistro(sinistro)) {
      toast.error('Sinistro finalizado não pode ter o status alterado');
      return;
    }

    if (novoStatus === "finalizado" && !canFinalizeSinistro()) {
      toast.error("Apenas administradores podem finalizar sinistros");
      return;
    }

    if (novoStatus === "finalizado") {
      if (getValorSinistroParaDespesa(sinistro) == null) {
        toast.error("Informe e salve o valor (estimado ou aprovado) antes de finalizar o sinistro.");
        return;
      }
      setSinistroPendenteFinalizar(sinistro);
      setShowConfirmFinalizar(true);
      return;
    }

    try {
      await atualizarStatus.mutateAsync({
        id: sinistroId.toString(),
        status: novoStatus as "aberto" | "analise" | "andamento" | "finalizado" | "rejeitado",
      });
      if (sinistroSelecionado?.id == sinistroId) {
        setSinistroSelecionado({ ...sinistroSelecionado, status: novoStatus });
      }
      toast.success("Status atualizado com sucesso!");
    } catch (error) {
      toast.error("Erro ao atualizar status");
      console.error("Erro ao atualizar status:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🔍 DEBUG - handleSubmit iniciado');
    console.log('🔍 DEBUG - Estado do formulário:', novoSinistro);
    
    // VALIDAÇÃO DOS CAMPOS OBRIGATÓRIOS
    if (!novoSinistro.cliente_id) {
      console.log('🔍 DEBUG - Cliente não selecionado');
      toast.error('Selecione um cliente');
      return;
    }
    
    if (!novoSinistro.tipo) {
      console.log('🔍 DEBUG - Tipo não selecionado');
      toast.error('Selecione o tipo de sinistro');
      return;
    }
    
    if (!novoSinistro.data_ocorrencia) {
      console.log('🔍 DEBUG - Data não informada');
      toast.error('Informe a data de ocorrência');
      return;
    }
    
    // Validação da data (não pode ser futura)
    const dataOcorrencia = new Date(novoSinistro.data_ocorrencia);
    const hoje = new Date();
    hoje.setHours(23, 59, 59, 999); // Fim do dia de hoje
    
    if (dataOcorrencia > hoje) {
      toast.error('A data de ocorrência não pode ser futura');
      return;
    }
    
    // Validação do valor estimado
    let valorEstimado = undefined;
    if (novoSinistro.valor_estimado) {
      console.log('🔍 DEBUG - Valor estimado original:', novoSinistro.valor_estimado);
      
      // Remover formatação brasileira (R$ 1.234,56 -> 1234.56)
      const valorLimpo = novoSinistro.valor_estimado
        .replace(/[R$\s]/g, '')           // Remove R$, espaços
        .replace(/\./g, '')               // Remove pontos de milhares
        .replace(',', '.');               // Converte vírgula para ponto
      
      console.log('🔍 DEBUG - Valor limpo:', valorLimpo);
      
      const valor = parseFloat(valorLimpo);
      console.log('🔍 DEBUG - Valor parseado:', valor);
      
      if (isNaN(valor) || valor <= 0) {
        console.log('🔍 DEBUG - Valor inválido');
        toast.error('Valor estimado deve ser um número positivo');
        return;
      }
      valorEstimado = valor;
      console.log('🔍 DEBUG - Valor final:', valorEstimado);
    }
    
    try {
      // Upload dos anexos primeiro
      let anexosUrls: string[] = [];
      
      if (anexos.length > 0) {
        setUploadingAnexos(true);
        console.log('🔍 DEBUG - Iniciando upload de', anexos.length, 'anexos');
        
        try {
          for (const file of anexos) {
            const fileName = `sinistros/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
            try {
              const { publicUrl } = await uploadFileToStorage(fileName, file, {
                buckets: ["uploads"],
              });
              anexosUrls.push(JSON.stringify({ url: publicUrl, nome: file.name }));
            } catch (uploadError) {
              const message = uploadError instanceof Error ? uploadError.message : "Erro desconhecido";
              toast.error(`Erro ao fazer upload de ${file.name}: ${message}`);
            }
          }
        } catch (uploadError) {
          console.error('❌ ERRO geral no upload:', uploadError);
          toast.error('Erro ao fazer upload dos anexos');
        } finally {
          setUploadingAnexos(false);
        }
      }
      
      const dadosParaCriar = {
        cliente_id: parseInt(novoSinistro.cliente_id),
        tipo: novoSinistro.tipo,
        descricao: novoSinistro.descricao || '',
        data_ocorrencia: novoSinistro.data_ocorrencia,
        valor_estimado: valorEstimado,
        anexos: anexosUrls.length > 0 ? anexosUrls : undefined
      };
      
      console.log('🔍 DEBUG - Dados para criar sinistro:', dadosParaCriar);
      console.log('🔍 DEBUG - Anexos URLs:', anexosUrls);
      console.log('🔍 DEBUG - Anexos incluídos:', dadosParaCriar.anexos);
      
      await criarSinistro.mutateAsync(dadosParaCriar);
      
      toast.success('Sinistro criado com sucesso!');
      
      // Limpar formulário
                    setNovoSinistro({
                cliente_id: "",
                tipo: "",
                descricao: "",
                data_ocorrencia: "",
                valor_estimado: ""
              });
              
              // Limpar anexos
              setAnexos([]);
              
              // Limpar pesquisa
      
      // Fechar modal
      setShowModalNovoSinistro(false);
      
    } catch (error: unknown) {
      console.error('❌ ERRO ao criar sinistro:', error);
      const msg = error instanceof Error ? error.message : String(error);

      if (msg.includes("anexos") && msg.includes("schema cache")) {
        toast.error(
          "Coluna de anexos não configurada no banco. Execute a migration 20260519000000_add_anexos_to_sinistros no Supabase (SQL Editor)."
        );
      } else if (msg.includes('cliente')) {
        toast.error('Cliente não encontrado');
      } else if (msg.includes('projeto')) {
        toast.error('Projeto não encontrado');
      } else if (msg.includes('tipo')) {
        toast.error('Tipo de sinistro inválido');
      } else {
        toast.error(msg || 'Erro ao criar sinistro. Tente novamente.');
      }
    }
  };

  const podeEditarNoModal =
    !!sinistroSelecionado && canEditSinistro(sinistroSelecionado) && !modalSomenteLeitura;

  const handleAbrirSinistro = (sinistro: any, somenteLeitura: boolean) => {
    if (!sinistro || !sinistro.id) {
      toast.error("Sinistro inválido");
      return;
    }
    setSinistroSelecionado(sinistro);
    setModalSomenteLeitura(somenteLeitura);
    inicializarCamposEdicao();
    setShowModalVisualizacao(true);
  };

  const handleFecharModalSinistro = () => {
    setShowModalVisualizacao(false);
    setSinistroSelecionado(null);
    setModalSomenteLeitura(false);
    resetCamposEdicao();
  };



  const gerarFormulario = () => {
    toast.success('Formulário de sinistro gerado!');
  };

  if (isLoading || !isProfileLoaded()) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Sinistros</h2>
          <p className="text-gray-600">Carregando...</p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">
            {isLoading ? 'Carregando sinistros...' : 'Carregando perfil do usuário...'}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Sinistros</h2>
          <p className="text-gray-600">Erro ao carregar dados</p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-red-500">Erro ao carregar sinistros: {error.message}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Sinistros</h2>
        <p className="text-gray-600">Gerencie sinistros e acompanhe o processo de resolução</p>
        

        
        <div className="flex justify-end items-center mt-4">
          <Button 
            onClick={() => {
              setShowModalNovoSinistro(true);
              // Limpar formulário
              setNovoSinistro({
                cliente_id: "",
                tipo: "",
                descricao: "",
                data_ocorrencia: "",
                valor_estimado: ""
              });
            }}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <Plus className="h-4 w-4 mr-2" />
             Novo Sinistro
          </Button>
        </div>
      </div>

      <Tabs defaultValue="lista" className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:grid-cols-2">
          <TabsTrigger value="lista">Lista de Sinistros</TabsTrigger>
          <TabsTrigger value="formulario">Formulário Padrão</TabsTrigger>
        </TabsList>

        <TabsContent value="lista" className="space-y-4">
          {/* Tabela de Sinistros */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <CardTitle className="flex items-center gap-2 text-gray-900">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  Lista de Sinistros ({totalSinistros})
                </CardTitle>
                <ListagemAcoes
                  title="Sinistros"
                  subtitle={`Página ${currentPage}/${totalPages}`}
                  rows={currentItems.map((sinistro: any) => {
                    const cliente_nome =
                      sinistro.cliente && typeof sinistro.cliente === "object" && sinistro.cliente.nome
                        ? sinistro.cliente.nome
                        : `Cliente ID: ${sinistro.cliente_id}`;
                    const cliente_estado = (sinistro.cliente as any)?.estado || "—";
                    const cliente_cidade = (sinistro.cliente as any)?.cidade || "—";
                    const cliente_localizacao = `${String(cliente_estado).trim()} / ${String(cliente_cidade).trim()}`;
                    const tipo_label = getTipoSinistroLabel(sinistro.tipo);
                    const status_label = getStatusLabel(sinistro.status || "aberto");
                    const totalValor = getValorTotalSinistro(sinistro);
                    const valor_label = totalValor != null ? `R$ ${formatarMoedaBr(totalValor)}` : "Não informado";
                    const data_label = sinistro.data_ocorrencia
                      ? format(new Date(sinistro.data_ocorrencia), "dd/MM/yyyy", { locale: ptBR })
                      : "Não informado";
                    return { ...sinistro, cliente_nome, cliente_estado, cliente_cidade, cliente_localizacao, tipo_label, status_label, valor_label, data_label } as SinistroRow;
                  })}
                  columns={sinistroColumns}
                  visibleKeys={visibleSinistroCols}
                  onToggleColumn={toggleSinistroCol}
                  filenameBase={`sinistros_p${currentPage}`}
                  enableExport
                  fetchAllRows={async () => {
                    const pageSize = 1000;
                    let offset = 0;
                    const all: any[] = [];

                    const clienteFilterPresent = !!(
                      (filtrosHook?.estado && filtrosHook.estado.trim()) ||
                      (filtrosHook?.cidade && filtrosHook.cidade.trim())
                    );

                    // eslint-disable-next-line no-constant-condition
                    while (true) {
                      let query = supabase
                        .from("sinistros")
                        .select(`*, cliente:clientes${clienteFilterPresent ? "!inner" : ""}(id, nome, email, telefone, cidade, estado)`)
                        .order("id", { ascending: false });

                      if (filtrosHook?.periodo?.dataInicio) query = query.gte("data_ocorrencia", filtrosHook.periodo.dataInicio);
                      if (filtrosHook?.periodo?.dataFim) query = query.lte("data_ocorrencia", filtrosHook.periodo.dataFim);
                      if (filtrosHook?.status) query = query.eq("status", filtrosHook.status);
                      if (filtrosHook?.tipo) query = query.eq("tipo", filtrosHook.tipo);
                      if (typeof filtrosHook?.valorMin === "number") query = query.gte("valor_estimado", filtrosHook.valorMin);
                      if (typeof filtrosHook?.valorMax === "number") query = query.lte("valor_estimado", filtrosHook.valorMax);
                      if (filtrosHook?.estado) query = query.eq("cliente.estado", filtrosHook.estado.toUpperCase());
                      if (filtrosHook?.cidade) query = query.eq("cliente.cidade", filtrosHook.cidade);

                      if (searchTerm && searchTerm.trim()) {
                        const searchLower = searchTerm.trim();
                        if (!isNaN(Number(searchLower))) {
                          const numSearch = parseInt(searchLower);
                          query = query.eq('id', numSearch);
                        } else {
                          const { data: clientesEncontrados } = await supabase
                            .from("clientes")
                            .select("id")
                            .or(`nome.ilike.%${searchLower}%`);
                          if (clientesEncontrados && clientesEncontrados.length > 0) {
                            const clienteIds = clientesEncontrados.map((c: any) => c.id);
                            query = query.or(`tipo.ilike.%${searchLower}%,cliente_id.in.(${clienteIds.join(",")})`);
                          } else {
                            query = query.ilike("tipo", `%${searchLower}%`);
                          }
                        }
                      }

                      const { data, error } = await query.range(offset, offset + pageSize - 1);
                      if (error) throw error;
                      const rows = data || [];
                      if (rows.length === 0) break;
                      all.push(...rows);
                      if (rows.length < pageSize) break;
                      offset += pageSize;
                    }

                    return all.map((sinistro: any) => {
                      const cliente_nome =
                        sinistro.cliente && typeof sinistro.cliente === "object" && sinistro.cliente.nome
                          ? sinistro.cliente.nome
                          : `Cliente ID: ${sinistro.cliente_id}`;
                      const cliente_estado = (sinistro.cliente as any)?.estado || "—";
                      const cliente_cidade = (sinistro.cliente as any)?.cidade || "—";
                      const cliente_localizacao = `${String(cliente_estado).trim()} / ${String(cliente_cidade).trim()}`;
                      const tipo_label = getTipoSinistroLabel(sinistro.tipo);
                      const status_label = getStatusLabel(sinistro.status || "aberto");
                      const totalValor = getValorTotalSinistro(sinistro);
                      const valor_label = totalValor != null ? `R$ ${formatarMoedaBr(totalValor)}` : "Não informado";
                      const data_label = sinistro.data_ocorrencia
                        ? format(new Date(sinistro.data_ocorrencia), "dd/MM/yyyy", { locale: ptBR })
                        : "Não informado";
                      return { ...sinistro, cliente_nome, cliente_estado, cliente_cidade, cliente_localizacao, tipo_label, status_label, valor_label, data_label } as SinistroRow;
                    });
                  }}
                />
              </div>
              <div className="mt-4 rounded-md bg-[#C9C9C9] p-3 text-xs">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-8">
                  <div className="lg:col-span-2">
                    <Label className="text-xs">Buscar</Label>
                    <Input
                      placeholder="ID, cliente ou tipo..."
                      value={searchTermInput}
                      onChange={(e) => setSearchTermInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          setSearchTerm(searchTermInput);
                        }
                      }}
                      className="h-8 text-xs"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Período - início</Label>
                    <Input
                      type="date"
                      className="h-8 text-xs"
                      value={filtrosDraft.dataInicio}
                      onChange={(e) => setFiltrosDraft((p) => ({ ...p, dataInicio: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Período - fim</Label>
                    <Input
                      type="date"
                      className="h-8 text-xs"
                      value={filtrosDraft.dataFim}
                      onChange={(e) => setFiltrosDraft((p) => ({ ...p, dataFim: e.target.value }))}
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select
                      value={filtrosDraft.status}
                      onValueChange={(v) => setFiltrosDraft((p) => ({ ...p, status: v }))}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        <SelectItem value="aberto">Aberto</SelectItem>
                        <SelectItem value="analise">Análise</SelectItem>
                        <SelectItem value="andamento">Andamento</SelectItem>
                        <SelectItem value="finalizado">Finalizado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs">Tipo</Label>
                    <Input
                      className="h-8 text-xs"
                      value={filtrosDraft.tipo}
                      onChange={(e) => setFiltrosDraft((p) => ({ ...p, tipo: e.target.value }))}
                      placeholder="Ex: roubo"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Valor (mín)</Label>
                    <Input
                      className="h-8 text-xs"
                      value={filtrosDraft.valorMin}
                      onChange={(e) => setFiltrosDraft((p) => ({ ...p, valorMin: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Valor (máx)</Label>
                    <Input
                      className="h-8 text-xs"
                      value={filtrosDraft.valorMax}
                      onChange={(e) => setFiltrosDraft((p) => ({ ...p, valorMax: e.target.value }))}
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Estado</Label>
                    <Select
                      value={filtrosDraft.estado || "todos"}
                      onValueChange={(v) => {
                        const next = v === "todos" ? "" : v;
                        setFiltrosDraft((p) => ({ ...p, estado: next, cidade: "" }));
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        {BRASIL_UFS.map((uf) => (
                          <SelectItem key={uf.sigla} value={uf.sigla}>
                            {uf.sigla} - {uf.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs">Cidade</Label>
                    <Select
                      value={filtrosDraft.cidade || "todas"}
                      onValueChange={(v) => setFiltrosDraft((p) => ({ ...p, cidade: v === "todas" ? "" : v }))}
                      disabled={!filtrosDraft.estado}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder={filtrosDraft.estado ? "Todas" : "Selecione um estado"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todas">Todas</SelectItem>
                        {cidadesDoEstado.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-3 flex justify-end gap-2">
                  <Button className="h-8 px-3 text-xs" variant="secondary" onClick={handleLimparFiltros}>
                    Limpar
                  </Button>
                  <Button className="h-8 px-3 text-xs bg-primary text-primary-foreground hover:bg-primary/90" onClick={handleAplicarFiltros}>
                    Aplicar filtro
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-gray-500">Carregando sinistros...</div>
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500">
                  <div className="text-red-500">Erro ao carregar sinistros: {error.message}</div>
                  <Button 
                    onClick={() => refetch()}
                    variant="outline"
                    className="mt-2"
                  >
                    Tentar Novamente
                  </Button>
                </div>
              ) : sinistrosFiltrados.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchTerm ||
                  !!filtrosHook?.status ||
                  !!filtrosHook?.tipo ||
                  !!filtrosHook?.estado ||
                  !!filtrosHook?.cidade ||
                  !!filtrosHook?.periodo?.dataInicio ||
                  !!filtrosHook?.periodo?.dataFim ||
                  typeof filtrosHook?.valorMin === "number" ||
                  typeof filtrosHook?.valorMax === "number"
                    ? "Nenhum sinistro encontrado com os filtros aplicados"
                    : "Nenhum sinistro registrado ainda"
                  }
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px] text-[11px] leading-tight">
                    <thead>
                      <tr className="border-b">
                        {visibleSinistroCols.includes("id") && <th className={cn(listagemThCenter, "w-24 whitespace-nowrap")}>ID</th>}
                        {visibleSinistroCols.includes("cliente") && <th className={cn(listagemThCenter, "w-48")}>Cliente</th>}
                        {visibleSinistroCols.includes("localizacao") && <th className={cn(listagemThCenter, "w-40 hidden md:table-cell whitespace-nowrap")}>Estado/Cidade</th>}
                        {visibleSinistroCols.includes("tipo") && <th className={cn(listagemThCenter, "w-32 hidden md:table-cell")}>Tipo</th>}
                        {visibleSinistroCols.includes("status") && <th className={cn(listagemThCenter, "w-24 whitespace-nowrap")}>Status</th>}
                        {visibleSinistroCols.includes("valor") && <th className={cn(listagemThCenter, "w-32 hidden lg:table-cell whitespace-nowrap")}>Valor</th>}
                        {visibleSinistroCols.includes("data") && <th className={cn(listagemThCenter, "w-24 hidden md:table-cell whitespace-nowrap")}>Data</th>}
                        <th className={cn(listagemThCenter, "w-24 whitespace-nowrap")}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((sinistro) => {
                        // Verificar se o sinistro é válido
                        if (!sinistro || !sinistro.id) {
                          console.error('Sinistro inválido encontrado:', sinistro);
                          return null;
                        }

                        return (
                          <tr key={sinistro.id} className={`border-b hover:bg-gray-50 ${
                            (sinistro.status || 'aberto') === 'finalizado'
                              ? 'bg-gray-50 opacity-75'
                              : ''
                          }`}>
                          {visibleSinistroCols.includes("id") && (
                            <td className={cn(listagemTdCenter, "text-gray-900 font-mono text-[11px] w-24 whitespace-nowrap")}>
                            {sinistro.id}
                            </td>
                          )}
                          {visibleSinistroCols.includes("cliente") && (
                            <td className={cn(listagemTdCenter, "text-gray-900 w-48")}>
                            {sinistro.cliente && typeof sinistro.cliente === 'object' && sinistro.cliente.nome 
                              ? sinistro.cliente.nome 
                              : `Cliente ID: ${sinistro.cliente_id}`
                            }
                            </td>
                          )}
                          {visibleSinistroCols.includes("localizacao") && (
                            <td className={cn(listagemTdCenter, "text-gray-600 w-40 hidden md:table-cell whitespace-nowrap")}>
                              {(sinistro.cliente as any)?.estado || "—"} / {(sinistro.cliente as any)?.cidade || "—"}
                            </td>
                          )}
                          {visibleSinistroCols.includes("tipo") && (
                            <td className={cn(listagemTdCenter, "text-gray-600 w-32 hidden md:table-cell")}>
                            {getTipoSinistroLabel(sinistro.tipo)}
                            </td>
                          )}
                          {visibleSinistroCols.includes("status") && (
                            <td className={cn(listagemTdCenter, "w-24")}>
                            <div className="flex justify-center">
                              <span className={`inline-block px-2 py-1 rounded-full text-xs ${getStatusColor(sinistro.status || 'aberto')}`}>
                                {getStatusLabel(sinistro.status || 'aberto')}
                              </span>
                            </div>
                            </td>
                          )}
                          {visibleSinistroCols.includes("valor") && (
                            <td className={cn(listagemTdCenter, "text-gray-900 font-semibold w-32 hidden lg:table-cell whitespace-nowrap")}>
                            {(() => {
                              const total = getValorTotalSinistro(sinistro);
                              return total != null ? `R$ ${formatarMoedaBr(total)}` : "Não informado";
                            })()}
                            </td>
                          )}
                          {visibleSinistroCols.includes("data") && (
                            <td className={cn(listagemTdCenter, "text-gray-600 w-24 hidden md:table-cell whitespace-nowrap")}>
                            {sinistro.data_ocorrencia 
                              ? format(new Date(sinistro.data_ocorrencia), 'dd/MM/yyyy', { locale: ptBR })
                              : 'Não informado'
                            }
                            </td>
                          )}
                          <td className={cn(listagemTdCenter, "w-24")}>
                            <div className="flex gap-1 justify-center">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                title="Editar"
                                disabled={!canEditSinistro(sinistro)}
                                onClick={() => handleAbrirSinistro(sinistro, false)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                title="Visualizar"
                                onClick={() => handleAbrirSinistro(sinistro, true)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {canDelete() && (
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Excluir sinistro"
                                  onClick={() => setSinistroParaExcluir({ id: Number(sinistro.id) })}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    </tbody>
                  </table>
                  
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-4">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Anterior
                      </Button>
                      <span className="text-gray-700">
                        Página {currentPage} de {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Próxima
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>



        <TabsContent value="formulario">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <FileText className="h-5 w-5 text-green-600" />
                Formulário Padrão de Sinistro
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 p-6 rounded-lg font-mono text-sm whitespace-pre-line">
{`FORMULÁRIO DE COMUNICAÇÃO DE SINISTRO
SUNSERVICE PROTEÇÃO LTDA.

DADOS DO SEGURADO:
Nome: [NOME_CLIENTE]
CPF: [CPF_CLIENTE]
Endereço: [ENDERECO_CLIENTE]
Telefone: [TELEFONE_CLIENTE]

DADOS DO SINISTRO:
Data do Sinistro: [DATA_SINISTRO]
Horário Aproximado: [HORA_SINISTRO]
Local do Sinistro: [LOCAL_SINISTRO]
Causa: [CAUSA_SINISTRO]

DESCRIÇÃO DO EVENTO:
[DESCRICAO_DETALHADA]

EQUIPAMENTOS DANIFICADOS:
- Módulos Fotovoltaicos: [QTD_MODULOS_DANIFICADOS]
- Inversores: [QTD_INVERSORES_DANIFICADOS]
- Outros: [OUTROS_DANOS]

VALOR ESTIMADO DOS DANOS: R$ [VALOR_ESTIMADO]

PROVIDÊNCIAS TOMADAS:
[PROVIDENCIAS]

DOCUMENTOS ANEXOS:
☐ Fotos dos danos
☐ Relatório técnico
☐ Nota fiscal dos equipamentos
☐ Outros: _________________

Declaro que as informações prestadas são verdadeiras.

Data: ___/___/_____

Assinatura: _________________________
           [NOME_CLIENTE]`}
              </div>
              
              <div className="mt-6 flex gap-2">
                <Button onClick={gerarFormulario} className="bg-primary hover:bg-primary/90">
                  Gerar Formulário Personalizado
                </Button>
                <Button variant="outline">
                  Download Template
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de edição / visualização do sinistro */}
      {showModalVisualizacao && sinistroSelecionado && (
        <div 
          className="fixed inset-0 z-[99999] bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ 
            zIndex: 99999,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header do Modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <FileTextIcon className="h-5 w-5 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  {podeEditarNoModal
                    ? `Editar Sinistro #${sinistroSelecionado.id}`
                    : `Visualizar Sinistro #${sinistroSelecionado.id}`}
                </h2>
              </div>
              <Button 
                onClick={handleFecharModalSinistro}
                variant="outline"
                size="sm"
                className="hover:bg-gray-100"
              >
                ✕
              </Button>
            </div>
            
            {/* Conteúdo do Modal */}
            <div className="p-6 space-y-6">
              {/* Informações Principais */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Cliente</Label>
                    <p className="text-lg font-semibold text-gray-900">
                      {sinistroSelecionado.cliente?.nome || 'Cliente não encontrado'}
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Tipo de Sinistro</Label>
                    <p className="text-lg text-gray-900">
                      {getTipoSinistroLabel(sinistroSelecionado.tipo || '')}
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Status</Label>
                    {podeEditarNoModal ? (
                      <Select
                        value={sinistroSelecionado.status || "aberto"}
                        onValueChange={(value) => handleStatusChange(sinistroSelecionado.id.toString(), value)}
                      >
                        <SelectTrigger className="mt-1 w-full max-w-[200px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="aberto">Aberto</SelectItem>
                          <SelectItem value="analise">Análise</SelectItem>
                          <SelectItem value="andamento">Andamento</SelectItem>
                          {canFinalizeSinistro() && (
                            <SelectItem value="finalizado">Finalizado</SelectItem>
                          )}
                          <SelectItem value="rejeitado">Rejeitado</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium ${
                        sinistroSelecionado.status === 'aberto' ? 'bg-yellow-100 text-yellow-800' :
                        sinistroSelecionado.status === 'analise' ? 'bg-blue-100 text-blue-800' :
                        sinistroSelecionado.status === 'andamento' ? 'bg-purple-100 text-purple-800' :
                        sinistroSelecionado.status === 'finalizado' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {getStatusLabel(sinistroSelecionado.status)}
                      </span>
                    )}
                  </div>
                  
                  {sinistroSelecionado.projeto_id && (
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Projeto (legado)</Label>
                      <p className="text-lg text-gray-900">
                        #{sinistroSelecionado.projeto_id}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Data de Ocorrência</Label>
                    <p className="text-lg text-gray-900">
                      {sinistroSelecionado.data_ocorrencia ? 
                        format(new Date(sinistroSelecionado.data_ocorrencia), 'dd/MM/yyyy', { locale: ptBR }) :
                        'Data não informada'
                      }
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Data de Criação</Label>
                    <p className="text-lg text-gray-900">
                      {sinistroSelecionado.created_at ? 
                        format(new Date(sinistroSelecionado.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR }) :
                        'Data não informada'
                      }
                    </p>
                  </div>
                </div>
              </div>

              {(() => {
                const itensValor = parseItensValorFromSinistro(sinistroSelecionado);
                const valorTotal = getValorTotalSinistro(sinistroSelecionado);
                return (
                  <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <Label className="text-sm font-medium text-gray-700">Valores do sinistro</Label>

                    {itensValor.length > 0 ? (
                      <div className="space-y-2">
                        {itensValor.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between gap-3 rounded-md border bg-white px-3 py-2 text-sm"
                          >
                            <span className="flex-1 text-gray-900">{item.descricao}</span>
                            <span className="font-semibold text-gray-900 whitespace-nowrap">
                              R$ {formatarMoedaBr(item.valor)}
                            </span>
                            {podeEditarNoModal && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 shrink-0 text-red-600"
                                title="Remover item"
                                disabled={atualizarSinistro.isPending}
                                onClick={() => handleRemoverItemValor(item.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Nenhum item de valor cadastrado.</p>
                    )}

                    {valorTotal != null && (
                      <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                        <span className="text-sm font-semibold text-gray-700">Valor total</span>
                        <span className="text-lg font-bold text-green-700">
                          R$ {formatarMoedaBr(valorTotal)}
                        </span>
                      </div>
                    )}

                    {podeEditarNoModal && (
                      <div className="space-y-3 border-t border-gray-200 pt-3">
                        <Label className="text-xs font-medium text-gray-600">Novo item</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-2 items-end">
                          <div>
                            <Label htmlFor="novoItemDescricao" className="text-xs text-gray-500">
                              Descrição
                            </Label>
                            <Input
                              id="novoItemDescricao"
                              value={novoItemDescricao}
                              onChange={(e) => setNovoItemDescricao(e.target.value)}
                              placeholder="Ex: 10 painéis luxen 585"
                              className="mt-1 bg-white"
                            />
                          </div>
                          <div>
                            <Label htmlFor="novoItemValor" className="text-xs text-gray-500">
                              Valor (R$)
                            </Label>
                            <Input
                              id="novoItemValor"
                              value={novoItemValor}
                              onChange={(e) => setNovoItemValor(e.target.value.replace(/[^\d,.]/g, ""))}
                              placeholder="0,00"
                              className="mt-1 bg-white"
                            />
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleAdicionarItemValor}
                            disabled={atualizarSinistro.isPending}
                            className="bg-primary hover:bg-primary/90"
                          >
                            {atualizarSinistro.isPending ? "Salvando..." : "Adicionar"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              
              {/* Descrição */}
              {sinistroSelecionado.descricao && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Descrição</Label>
                  <p className="text-gray-900 bg-gray-50 p-4 rounded-lg mt-2">
                    {sinistroSelecionado.descricao}
                  </p>
                </div>
              )}
              

              
              {/* Anexos */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  Documentos anexos
                </Label>
                {sinistroSelecionado.anexos &&
                sinistroSelecionado.anexos.filter((item: string) => !isItemValorAnexo(item)).length > 0 ? (
                  <div className="space-y-1.5">
                    {sinistroSelecionado.anexos
                      .filter((item: string) => !isItemValorAnexo(item))
                      .map((item: string, i: number) => {
                      const anexo = parseAnexoSinistro(item);
                      return (
                        <div
                          key={i}
                          className="flex items-center justify-between gap-2 py-2 px-2.5 rounded-md border bg-gray-50 hover:bg-gray-100"
                        >
                          <span className="flex items-center gap-2 min-w-0 truncate text-sm">
                            <FileTextIcon className="h-4 w-4 text-blue-600 shrink-0" />
                            <span className="truncate font-medium" title={anexo.nome}>
                              {anexo.nome}
                            </span>
                          </span>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 text-blue-600"
                              title="Visualizar"
                              onClick={() => void openStorageUrl(anexo.url)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              title="Baixar"
                              onClick={() => {
                                const link = document.createElement("a");
                                link.href = anexo.url;
                                link.download = anexo.nome;
                                link.target = "_blank";
                                link.rel = "noopener noreferrer";
                                link.click();
                              }}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
                    Nenhum documento anexado.
                  </p>
                )}

                {podeEditarNoModal && (
                  <div className="space-y-3 pt-2 border-t border-gray-200">
                    <Label className="text-sm font-medium text-gray-700">Adicionar novos anexos</Label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        type="file"
                        multiple
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={handleFileSelectEdit}
                        className="hidden"
                        id="file-upload-edit"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById("file-upload-edit")?.click()}
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        Selecionar arquivos
                      </Button>
                      <span className="text-xs text-gray-500">JPG, PNG ou PDF (máx. 20MB)</span>
                    </div>
                    {anexosEdit.length > 0 && (
                      <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
                        {anexosEdit.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                            <span className="text-sm truncate">{file.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeAnexoEdit(index)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <div className="flex justify-end pt-1">
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleSalvarAnexos}
                            disabled={uploadingAnexosEdit || atualizarSinistro.isPending}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            {uploadingAnexosEdit ? "Enviando..." : "Salvar anexos"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Aviso para sinistros finalizados */}
              {(sinistroSelecionado.status || 'aberto') === 'finalizado' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <h3 className="text-lg font-semibold text-yellow-900">Sinistro Finalizado</h3>
                  </div>
                  <p className="text-yellow-800 mt-2">
                    Este sinistro está finalizado. O status não pode mais ser alterado.
                  </p>
                </div>
              )}
              
              {/* Comentários */}
              {podeEditarNoModal && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Adicionar comentário</Label>
                    <div className="flex gap-2 mt-2">
                      <Textarea
                        placeholder="Registre o andamento do sinistro..."
                        value={comentario}
                        onChange={(e) => setComentario(e.target.value)}
                        className="flex-1"
                        rows={3}
                      />
                      <Button
                        onClick={handleAdicionarComentario}
                        disabled={criarComentario.isPending}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        {criarComentario.isPending ? "Salvando..." : "Salvar"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Histórico de Comentários */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Histórico de Comentários</h3>
                <SinistroHistorico sinistroId={Number(sinistroSelecionado.id)} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Novo Sinistro */}
      {showModalNovoSinistro && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 999999 }}>
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto sinistro-modal" style={{ zIndex: 999999 }}>
            {/* Header do Modal */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-red-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Registrar Novo Sinistro
                </h2>
              </div>
              <Button 
                onClick={() => setShowModalNovoSinistro(false)}
                variant="outline"
                size="sm"
                className="hover:bg-gray-100"
              >
                ✕
              </Button>
            </div>
            
            {/* Conteúdo do Modal */}
            <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ClienteSearchSelect
                    label="Cliente"
                    required
                    value={novoSinistro.cliente_id}
                    onChange={(clienteId) => {
                      setNovoSinistro({
                        ...novoSinistro,
                        cliente_id: clienteId,
                      });
                    }}
                  />
                  
                  <div>
                    <Label htmlFor="tipo">Tipo de Sinistro *</Label>
                    <div className="mt-1">
                      <Select 
                        value={novoSinistro.tipo} 
                        onValueChange={(value) => {
                          console.log('🔍 DEBUG - Tipo selecionado:', value);
                          setNovoSinistro({...novoSinistro, tipo: value});
                        }}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Selecione o tipo de sinistro" />
                        </SelectTrigger>
                        <SelectContent style={{ zIndex: 9999999 }}>
                          <SelectItem value="incendio">Incêndio</SelectItem>
                          <SelectItem value="vendaval">Vendaval</SelectItem>
                          <SelectItem value="granizo">Granizo</SelectItem>
                          <SelectItem value="raio">Dano Elétrico (Raio)</SelectItem>
                          <SelectItem value="explosao">Explosão</SelectItem>
                          <SelectItem value="roubo_furto">Roubo/Furto</SelectItem>
                          <SelectItem value="dano_eletrico">Dano Elétrico</SelectItem>
                          <SelectItem value="inundacao">Inundação</SelectItem>
                          <SelectItem value="queda_arvore">Queda de Árvore</SelectItem>
                          <SelectItem value="acidente_veiculo">Acidente com Veículo</SelectItem>
                          <SelectItem value="vandalismo">Vandalismo</SelectItem>
                          <SelectItem value="fenomeno_natural">Fenômeno Natural</SelectItem>
                          <SelectItem value="outros">Outros</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="data">Data da Ocorrência *</Label>
                    <Input
                      id="data"
                      type="date"
                      value={novoSinistro.data_ocorrencia}
                      max={dataMaxima}
                      onChange={(e) => {
                        console.log('🔍 DEBUG - Data alterada:', e.target.value);
                        setNovoSinistro({...novoSinistro, data_ocorrencia: e.target.value});
                      }}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Data máxima: {new Date().toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="valorEstimado">Valor Estimado</Label>
                    <Input
                      id="valorEstimado"
                      value={novoSinistro.valor_estimado}
                      onChange={(e) => {
                        // Permitir apenas números, vírgulas e pontos
                        const valor = e.target.value.replace(/[^\d,.]/g, '');
                        setNovoSinistro({...novoSinistro, valor_estimado: valor});
                      }}
                      onBlur={(e) => {
                        // Formatar ao sair do campo
                        if (e.target.value) {
                          const valor = e.target.value.replace(/[^\d,]/g, '').replace(',', '.');
                          const numero = parseFloat(valor);
                          if (!isNaN(numero)) {
                            const formatado = numero.toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            });
                            setNovoSinistro({...novoSinistro, valor_estimado: `R$ ${formatado}`});
                          }
                        }
                      }}
                      placeholder="R$ 0,00"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="descricao">Descrição Detalhada</Label>
                  <Textarea
                    id="descricao"
                    value={novoSinistro.descricao}
                    onChange={(e) => setNovoSinistro({...novoSinistro, descricao: e.target.value})}
                    placeholder="Descreva detalhadamente o que aconteceu..."
                    rows={4}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Anexar Fotos/Documentos</Label>
                  <div className="mt-1 space-y-3">
                    {/* Botão de Upload */}
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        multiple
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="file-upload"
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        className="flex items-center gap-2"
                        onClick={() => {
                          console.log('🔍 DEBUG - Botão Selecionar Arquivos clicado');
                          document.getElementById('file-upload')?.click();
                        }}
                      >
                        <Upload className="h-4 w-4" />
                        Selecionar Arquivos
                      </Button>
                      <span className="text-sm text-gray-500">JPG, PNG ou PDF (máx. 20MB)</span>
                    </div>
                    
                    {/* Lista de Arquivos Selecionados */}
                    {anexos.length > 0 && (
                      <div className="border rounded-lg p-3 bg-gray-50">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Arquivos Selecionados ({anexos.length})
                        </h4>
                        <div className="space-y-2">
                          {anexos.map((file, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                              <div className="flex items-center gap-2">
                                <FileTextIcon className="h-4 w-4 text-blue-600" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                  <p className="text-xs text-gray-500">
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                  </p>
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeAnexo(index)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button 
                    type="button"
                    onClick={() => setShowModalNovoSinistro(false)}
                    variant="outline"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-red-600 hover:bg-red-700 text-white px-8"
                    disabled={criarSinistro.isPending || uploadingAnexos}
                  >
                    {uploadingAnexos ? 'Fazendo Upload...' : 
                     criarSinistro.isPending ? 'Cadastrando...' : 'Cadastrar Sinistro'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <AlertDialog
        open={showConfirmFinalizar}
        onOpenChange={(open) => {
          if (!open) limparFluxoFinalizar();
          else setShowConfirmFinalizar(open);
        }}
      >
        <AlertDialogContent className="max-w-md gap-5">
          <AlertDialogHeader className="space-y-3">
            <AlertDialogTitle className="flex items-center gap-2 text-left">
              <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
              Finalizar sinistro #{sinistroPendenteFinalizar?.id}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left leading-relaxed">
              O sinistro será marcado como <span className="font-medium text-foreground">Finalizado</span>, o
              status não poderá mais ser alterado e uma <span className="font-medium text-foreground">conta a pagar</span>{" "}
              será gerada automaticamente no financeiro.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {sinistroPendenteFinalizar && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Resumo do sinistro</p>
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm">
                <dt className="text-gray-500">Cliente</dt>
                <dd className="font-medium text-gray-900 text-right">
                  {sinistroPendenteFinalizar.cliente?.nome ??
                    `ID ${sinistroPendenteFinalizar.cliente_id}`}
                </dd>
                {getValorSinistroParaDespesa(sinistroPendenteFinalizar) != null && (
                  <>
                    <dt className="text-gray-500">Valor</dt>
                    <dd className="font-medium text-gray-900 text-right">
                      R${" "}
                      {getValorSinistroParaDespesa(sinistroPendenteFinalizar)!.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                      })}
                      <span className="ml-1 text-xs font-normal text-gray-500">
                        ({sinistroPendenteFinalizar.valor_aprovado ? "aprovado" : "estimado"})
                      </span>
                    </dd>
                  </>
                )}
                {sinistroPendenteFinalizar.tipo && (
                  <>
                    <dt className="text-gray-500">Tipo</dt>
                    <dd className="text-gray-900 text-right">
                      {getTipoSinistroLabel(sinistroPendenteFinalizar.tipo)}
                    </dd>
                  </>
                )}
              </dl>
            </div>
          )}

          <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            <AlertDialogCancel disabled={atualizarStatus.isPending} className="mt-0">
              Cancelar
            </AlertDialogCancel>
            <Button
              type="button"
              className="bg-primary hover:bg-primary/90"
              disabled={atualizarStatus.isPending}
              onClick={handleConfirmarFinalizar}
            >
              {atualizarStatus.isPending ? "Finalizando..." : "Finalizar sinistro"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!sinistroParaExcluir}
        onOpenChange={(open) => { if (!open) setSinistroParaExcluir(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir sinistro?</AlertDialogTitle>
            <AlertDialogDescription>
              O sinistro #{sinistroParaExcluir?.id} e todos os comentários e transações
              financeiras vinculadas serão removidos permanentemente. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (!sinistroParaExcluir) return;
                excluirSinistro.mutateAsync(sinistroParaExcluir.id)
                  .then(() => {
                    toast.success("Sinistro excluído com sucesso");
                    if (sinistroSelecionado?.id === sinistroParaExcluir.id) {
                      setShowModalVisualizacao(false);
                      setSinistroSelecionado(null);
                    }
                    setSinistroParaExcluir(null);
                  })
                  .catch((e) => toast.error(e?.message || "Erro ao excluir sinistro"));
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
